import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Modal, Platform, Alert } from 'react-native';
import { Check, X } from 'lucide-react-native';
import * as Updates from 'expo-updates';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

interface LanguageDefinition {
  code: string;
  name: string;
  nativeName: string;
}

const allLanguages: Record<string, LanguageDefinition> = {
  am: { code: 'am', name: 'Amharic', nativeName: 'አማርኛ' },
  ar: { code: 'ar', name: 'العربية', nativeName: 'عربي' },
  de: { code: 'de', name: 'German', nativeName: 'Deutsch' },
  en: { code: 'en', name: 'English', nativeName: 'English' },
  es: { code: 'es', name: 'Spanish', nativeName: 'Español' },
  fr: { code: 'fr', name: 'French', nativeName: 'Français' },
  it: { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  ms: { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu' },
  nl: { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  ro: { code: 'ro', name: 'Romanian', nativeName: 'Română' },
  th: { code: 'th', name: 'Thai', nativeName: 'ไทย' },
  vi: { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt' },
};

const defaultLanguageCodes = ['am', 'ar', 'en', 'fr', 'es', 'it', 'nl', 'ro'];

interface LanguageSelectorProps {
  visible?: boolean;
  onClose?: () => void;
  availableLanguages?: string[];
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ visible, onClose, availableLanguages }) => {
  const { language, setLanguage, t, isRTL, flexDirection } = useTranslation();
  const { authState } = useAuth();
  const { colors: { primary: primaryColor } } = useTheme();

  const codes = (availableLanguages && availableLanguages.length > 0)
    ? availableLanguages
    : defaultLanguageCodes;

  const displayedLanguages = codes
    .map(code => allLanguages[code])
    .filter((lang): lang is LanguageDefinition => !!lang)
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleLanguageSelect = async (languageCode: string) => {
    const isChangingLanguage = language !== languageCode;

    if (!isChangingLanguage) {
      onClose?.();
      return;
    }

    await setLanguage(languageCode as any, authState.user?.smartuserId || null);
    onClose?.();

    if (Platform.OS !== 'web') {
      Alert.alert(
        t('restartRequired'),
        t('restartToApplyLanguage'),
        [
          { text: t('cancel'), style: 'cancel' },
          {
            text: t('ok'),
            onPress: async () => {
              try {
                await Updates.reloadAsync();
              } catch (error) {
                console.warn('[LanguageSelector] reloadAsync failed:', error);
              }
            },
          },
        ],
      );
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          <View style={[styles.header, { flexDirection }]}>
            <Text style={styles.title}>{t('selectLanguage')}</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <X size={24} color="#ffffff" />
            </TouchableOpacity>
          </View>

          <View style={styles.languageList}>
            {displayedLanguages.map((lang) => (
              <TouchableOpacity
                key={lang.code}
                style={[styles.languageItem, { flexDirection }]}
                onPress={() => handleLanguageSelect(lang.code)}
              >
                <View style={styles.languageInfo}>
                  <Text style={styles.languageName}>{lang.name}</Text>
                  <Text style={styles.languageNative}>{lang.nativeName}</Text>
                </View>
                {language === lang.code && (
                  <Check size={20} color={primaryColor} />
                )}
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  container: {
    backgroundColor: '#2a2a2a',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  title: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  languageList: {
    padding: 20,
  },
  languageItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  languageInfo: {
    flex: 1,
  },
  languageName: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 2,
  },
  languageNative: {
    color: '#888',
    fontSize: 14,
  },
});